import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navigation, MapPin, QrCode, FileText, ClipboardList } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Header from '../components/Dashboard/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import Modal from '../components/ui/Modal';

import IncidentMap from '../components/Incident/IncidentMap';
import METHANE from '../components/Incident/METHANE';
import QuickActions from '../components/Incident/QuickActions';

import AnnouncementService from '../services/AnnouncementService';
import AnnouncementMemberService from '../services/AnnouncementMemberService';
import TeamCard from '../components/Incident/TeamCard';
import MemberCard from '../components/Teams/MemberCard';
import ResponseSummary from '../components/Incident/ResponseSummary';

const INCIDENT_TABS = [
    { value: 'methane', label: 'METHANE' },
    { value: 'map', label: 'Map & Location' },
    { value: 'teams', label: 'Teams' },
    { value: 'patients', label: 'Patients / Decon' },
    { value: 'stakeholders', label: 'Stakeholders' },
    { value: 'handoff', label: 'Handoff / Reports' },
];

function Incident() {
    let { id } = useParams();
    const [announcement, setAnnouncement] = useState({});
    const [members, setMembers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [assignedTeams, setAssignedTeams] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [action, setAction] = useState("assignTeam");
    const [tab, setTab] = useState('methane');
    const [closeOpen, setCloseOpen] = useState(false);
    const [closeNotes, setCloseNotes] = useState('');

    const STATUS_BADGE = { Pending: 'amber', Active: 'green', Closed: 'neutral', Rejected: 'red' };

    const handleActivate = async () => {
        try {
            const { data } = await AnnouncementService.activate(id);
            toast.success(`Event activated. ${data.notified || 0} member(s) notified.`);
            loadAnnouncement();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Activation failed.');
        }
    };

    const handleClose = async () => {
        try {
            await AnnouncementService.close(id, closeNotes);
            toast.success('Event closed.');
            setCloseOpen(false);
            loadAnnouncement();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Close failed.');
        }
    };

    const loadMembers = async () => {
        const response = await AnnouncementMemberService.findById(id);

        if (response.error) {
            toast.error("Something went wrong!");
            return;
        }
        console.log("members", response.entity);
        setMembers(response.entity);

        // Extract unique teams (filter out null teams)
        const uniqueTeams = response.entity
            .map(item => item.team)
            .filter(Boolean)
            .filter((team, index, self) =>
                index === self.findIndex(t => t.id === team.id)
            );

        // Process each team to get the required format
        const processedTeams = uniqueTeams.map(team => {
            // Filter members belonging to the current team
            const teamMembers = response.entity.filter(item => item.team && item.team.id === team.id);

            // Count total members and members by role
            const roleCounts = teamMembers.reduce((acc, item) => {
                acc.total += 1;
                const role = item.member.role.role_name.toLowerCase();
                if (acc[role]) {
                    acc[role] += 1;
                } else {
                    acc[role] = 1;
                }
                return acc;
            }, { total: 0 });

            return {
                id: team.id,
                name: team.name,
                totalMembers: roleCounts.total,
                admins: roleCounts.admins || 0,
                doctors: roleCounts.doctors || 0,
                nurses: roleCounts.nurses || 0,
                rsos: roleCounts.rsos || 0
            };
        });

        console.log("processed members", processedTeams);
        setAssignedTeams(processedTeams)
    };

    const loadAnnouncement = async () => {
        const data = await AnnouncementService.findById(id);

        if (data.data.error) {
            toast.error("Something went wrong!");
            return;
        }
        setAnnouncement(data.data);

    };

    const handelViewTeam = (id) => {
        // eslint-disable-next-line eqeqeq
        const data = members.filter((item) => item.teamId == id);
        setTeamMembers(data)
        setAction("viewMembers")
    }

    // useEffect(() => {
    //     loadAnnouncement();
    //     loadMembers();
    // }, [id]);



    useEffect(() => {
        // Load announcements initially
        loadAnnouncement();
        loadMembers();

        // Set up a 30-second interval to load announcements
        const intervalId = setInterval(() => {
            console.log("updating every 30 secs");
            // loadAnnouncement();
            loadMembers();
        }, 30000);

        // Clear the interval when the component unmounts
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <Sidebar title={`${announcement.drill ? "Drill" : "Announcement"}- ${announcement.title}`} header={<Header />}>
            <Card padded={false} className="overflow-hidden">
                <div className="px-6 py-5 border-b border-neutral-100 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            {announcement.id != null && <Badge variant="neutral">#{announcement.id}</Badge>}
                            {announcement.incident_type?.incident_type_name && (
                                <Badge variant="red">{announcement.incident_type.incident_type_name}</Badge>
                            )}
                            <Badge variant={announcement.drill ? 'amber' : 'green'}>
                                {announcement.drill ? 'Drill' : 'Live Incident'}
                            </Badge>
                            {announcement.status && (
                                <Badge variant={STATUS_BADGE[announcement.status] || 'neutral'}>{announcement.status}</Badge>
                            )}
                        </div>
                        <h3 className="font-bold text-xl text-neutral-900">{announcement.title || 'Incident'}</h3>
                        <p className="text-sm text-neutral-500 mt-1">
                            {announcement.description || 'Interactive navigation for METHANE, map, teams, patients, decon, stakeholders and reporting.'}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {announcement.status === 'Pending' && (
                            <Button variant="primary" onClick={handleActivate}>
                                Activate &amp; Notify Teams
                            </Button>
                        )}
                        {announcement.status === 'Active' && (
                            <Button variant="secondary" onClick={() => setCloseOpen(true)}>
                                Close Event
                            </Button>
                        )}
                        {announcement.status === 'Closed' && (
                            <Badge variant="neutral">
                                Closed {announcement.closedAt ? new Date(announcement.closedAt).toLocaleString() : ''}
                            </Badge>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    <Tabs tabs={INCIDENT_TABS} active={tab} onChange={setTab} className="mb-6" />

                    {tab === 'methane' && <METHANE announcement={announcement} />}

                    {tab === 'map' && (
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                            <div className="xl:col-span-2 rounded-xl border border-neutral-200 overflow-hidden relative h-[420px]">
                                <IncidentMap lat={announcement.lat} lng={announcement.lng} members={members} />
                            </div>
                            <div className="space-y-3">
                                <div className="text-sm text-neutral-600">
                                    <b className="text-neutral-900">Exact Location:</b> {announcement.location || '—'}
                                </div>
                                <div className="text-sm text-neutral-600">
                                    <b className="text-neutral-900">Coordinates:</b> {announcement.lat ?? '—'}, {announcement.lng ?? '—'}
                                </div>
                                <div className="text-sm text-neutral-600">
                                    <b className="text-neutral-900">Nearest Landmark:</b> {announcement.landmark || '—'}
                                </div>
                                <button className="action-button" onClick={() => toast.success('Coordinates captured from Google Map')}>
                                    <Navigation className="w-4 h-4 mr-2" /> Capture Google Coordinates
                                </button>
                                <button className="action-button" onClick={() => toast.success('Nearest teams recalculated')}>
                                    <MapPin className="w-4 h-4 mr-2" /> Calculate Team Distance
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === 'teams' && (
                        <div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                                <div>
                                    <h4 className="font-bold text-neutral-900">Team Assignment &amp; Response</h4>
                                    <p className="text-xs text-neutral-500 mt-1">Assign responding teams and track member acceptance.</p>
                                </div>
                                <QuickActions setTeams={setTeams} announcementId={id} setAction={setAction} />
                            </div>

                            {action === "viewTeam" && teams && (
                                <TeamCard assigned={false} teams={teams} setTeams={setTeams} announcementId={id} handelViewTeam={handelViewTeam} />
                            )}
                            {action === "assignTeam" && members && assignedTeams && (
                                <TeamCard assigned={true} teams={assignedTeams} setTeams={setAssignedTeams} announcementId={id} handelViewTeam={handelViewTeam} />
                            )}
                            {action === "viewMembers" && assignedTeams && (
                                <div>
                                    <Button variant="ghost" size="sm" className="mb-4" onClick={() => setAction("assignTeam")}>
                                        &larr; Back to Teams
                                    </Button>
                                    <MemberCard members={teamMembers} />
                                </div>
                            )}
                            {action === "viewResponses" && <ResponseSummary members={members} />}
                        </div>
                    )}

                    {tab === 'patients' && (
                        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                            <table className="w-full text-left">
                                <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider font-semibold">
                                    <tr>
                                        <th className="px-6 py-4">Patient ID</th>
                                        <th className="px-6 py-4">Triage</th>
                                        <th className="px-6 py-4">Vitals</th>
                                        <th className="px-6 py-4">CBRNE</th>
                                        <th className="px-6 py-4">Decon</th>
                                        <th className="px-6 py-4">Destination</th>
                                        <th className="px-6 py-4">Sync</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-100 text-sm">
                                    <tr>
                                        <td className="px-6 py-8 text-center text-neutral-400" colSpan={7}>
                                            No patient records synced for this incident yet.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === 'stakeholders' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            <div className="strategy-card text-center text-sm text-neutral-400 py-10 md:col-span-2 xl:col-span-3">
                                No stakeholders linked to this incident yet.
                            </div>
                        </div>
                    )}

                    {tab === 'handoff' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="strategy-card">
                                <h4 className="font-bold text-neutral-900">QR/NFC Handoff</h4>
                                <p className="text-sm text-neutral-500 mt-2">Generate patient transfer packages for the ambulance / receiving team.</p>
                                <button className="action-button mt-4" onClick={() => toast.success('QR/NFC package generated')}>
                                    <QrCode className="w-4 h-4 mr-2" /> Generate
                                </button>
                            </div>
                            <div className="strategy-card">
                                <h4 className="font-bold text-neutral-900">PDF Report</h4>
                                <p className="text-sm text-neutral-500 mt-2">Export command, team, patient, decon and stakeholder report.</p>
                                <button className="action-button mt-4" onClick={() => toast.success('PDF report exported')}>
                                    <FileText className="w-4 h-4 mr-2" /> Export
                                </button>
                            </div>
                            <div className="strategy-card">
                                <h4 className="font-bold text-neutral-900">After Action</h4>
                                <p className="text-sm text-neutral-500 mt-2">Capture drill/event performance and improvement actions.</p>
                                <button className="action-button mt-4" onClick={() => toast.success('After-action record opened')}>
                                    <ClipboardList className="w-4 h-4 mr-2" /> Open
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            <Modal
                open={closeOpen}
                onClose={() => setCloseOpen(false)}
                title="Close Event"
                subtitle="Stand down teams and archive this event. Assigned members are notified."
                confirmLabel="Close Event"
                onConfirm={handleClose}
            >
                <div>
                    <label className="form-label" htmlFor="close-notes">Closure Notes</label>
                    <textarea
                        id="close-notes"
                        className="form-input"
                        rows={4}
                        placeholder="Outcome summary, lessons learned, remaining actions..."
                        value={closeNotes}
                        onChange={(e) => setCloseNotes(e.target.value)}
                    />
                </div>
            </Modal>
        </Sidebar>
    );
}

export default Incident;
